import {
  BasicDeviceGroupData,
  Device,
  DeviceData,
  DeviceGroupData,
  MoveDevicesData,
  PartialBy,
} from '@zspace/types';
import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import { FaFolder, FaObjectUngroup, FaQrcode } from 'react-icons/fa6';
import Conditional from '../../shared/conditional/conditional';
import useHttpRequest from '../../shared/hooks/http-request';
import useToast from '../../shared/hooks/toasts';
import If from '../../shared/if/if';
import Button from '../../ui/button/button';
import FeedbackMessage from '../../ui/feedback-message/feedback-message';
import Modal from '../../ui/modal/modal';
import { RemoveDevicesModalContent } from '../remove-devices-modal/remove-devices-modal';
import { MoveDevicesFormData, initialFormData, validateForm } from './form';
import MoveDevicesDeviceGroupsLayout from './move-devices-device-groups-layout/move-devices-device-groups-layout';
import MoveDevicesGroupCodeLayout from './move-devices-group-code-layout/move-devices-group-code-layout';
import MoveDevicesOptionItem from './move-devices-option-item/move-devices-option-item';

export enum MOVE_DEVICE_OPTIONS {
  UNGROUPED = 'Ungrouped',
  DEVICE_GROUP = 'Device groups',
  GROUP_CODE = 'Group code',
}

const MODAL_TITLE = 'Move devices';
const CANCEL_BUTTON_TEXT = 'Cancel';
const BACK_BUTTON_TEXT = 'Back';
const GROUP_CODE_NOT_FOUND_ERROR_MESSAGE =
  'Group code not found. Please try again with a valid group code';
const OPTION_NOT_SELECTED_ERROR_MESSAGE = 'Please select an option';

export type MoveDevicesModalProps = {
  show: boolean;
  selectedDevices: Device[] | DeviceData[];
  deviceGroupsToBeEmptied?: DeviceGroupData[];
  deviceGroups: BasicDeviceGroupData[];
  onMoveDevicesToDeviceGroup: (
    moveDevicesData: PartialBy<MoveDevicesData, 'devicesIds'>
  ) => Promise<void>;
  onRemoveDevicesFromDeviceGroup?: () => void;
  onCancel: () => void;
};

export function MoveDevicesModal({
  show,
  selectedDevices,
  deviceGroupsToBeEmptied,
  deviceGroups,
  onMoveDevicesToDeviceGroup,
  onRemoveDevicesFromDeviceGroup,
  onCancel,
}: MoveDevicesModalProps) {
  const [selectedMoveOption, setSelectedMoveOption] = useState<
    MOVE_DEVICE_OPTIONS | undefined
  >();
  const [selectedDeviceGroup, setSelectedDeviceGroup] = useState<
    string | undefined
  >();
  const [formData, setFormData] =
    useState<MoveDevicesFormData>(initialFormData);
  const { executeHttpRequest: executeMoveDevices, isLoading: isMovingDevices } =
    useHttpRequest();
  const [emptyGroupWarningAccepted, setEmptyGroupWarningAccepted] =
    useState(false);
  const [submitButtonPressed, setSubmitButtonPressed] =
    useState<boolean>(false);
  const [showEmptyGroupWarning, setShowEmptyGroupWarning] = useState(false);
  const toast = useToast();

  const devicesText = useMemo(
    () => (selectedDevices.length === 1 ? 'device' : 'devices'),
    [selectedDevices.length]
  );

  const subtitleText = useMemo(() => {
    if (selectedMoveOption === MOVE_DEVICE_OPTIONS.GROUP_CODE) {
      return 'Please input your group code';
    }
    return `Where do you want to move the selected ${devicesText}?`;
  }, [devicesText, selectedMoveOption]);

  const warningButtonConfirmationText = useMemo(
    () => `Yes, move ${devicesText}`,
    [devicesText]
  );

  const submitButtonText = useMemo(() => `Move ${devicesText}`, [devicesText]);

  const moveDevicesErrorMessage = useMemo(
    () => `The ${devicesText} could not be moved. Please try again`,
    [devicesText]
  );

  const deviceGroupCode = useMemo(
    () => formData.deviceGroupCode.value ?? '',
    [formData.deviceGroupCode.value]
  );

  const showErrorMessage = useMemo(() => {
    let isInvalidSelection = !selectedMoveOption;
    if (selectedMoveOption === MOVE_DEVICE_OPTIONS.DEVICE_GROUP) {
      isInvalidSelection = isInvalidSelection || !selectedDeviceGroup;
    }
    return submitButtonPressed && isInvalidSelection;
  }, [selectedDeviceGroup, selectedMoveOption, submitButtonPressed]);

  const moveDevicesOptionsData = useMemo(() => {
    return [
      {
        value: MOVE_DEVICE_OPTIONS.UNGROUPED,
        icon: <FaObjectUngroup />,
      },
      {
        value: MOVE_DEVICE_OPTIONS.DEVICE_GROUP,
        icon: <FaFolder />,
        hasNestedSelection: true,
      },
      {
        value: MOVE_DEVICE_OPTIONS.GROUP_CODE,
        icon: <FaQrcode />,
        hasNestedSelection: true,
      },
    ];
  }, []);

  const resetData = useCallback(() => {
    setSelectedMoveOption(undefined);
    setSelectedDeviceGroup(undefined);
    setSubmitButtonPressed(false);
    setShowEmptyGroupWarning(false);
    setEmptyGroupWarningAccepted(false);
    setFormData(initialFormData);
  }, []);

  const handleSelectDeviceGroup = useCallback((id: string) => {
    setSelectedDeviceGroup(id);
  }, []);

  const handleMoveDevices = useCallback(
    async () =>
      executeMoveDevices({
        asyncFunction: async () => {
          setSubmitButtonPressed(true);
          let isValidData = !!selectedMoveOption;
          let deviceGroupId;
          const pendingEmptyGroupWarning =
            !!deviceGroupsToBeEmptied?.length &&
            !showEmptyGroupWarning &&
            !emptyGroupWarningAccepted;

          switch (selectedMoveOption) {
            case MOVE_DEVICE_OPTIONS.GROUP_CODE: {
              const formValidation = Object.keys(formData).reduce(
                (acc, key) => {
                  const formDataKey = key as keyof MoveDevicesFormData;
                  const error = validateForm[formDataKey]?.(
                    formData[formDataKey].value
                  );
                  setFormData((prev) => ({
                    ...prev,
                    [formDataKey]: {
                      ...prev[formDataKey],
                      error,
                      touched: true,
                    },
                  }));
                  return {
                    values: {
                      ...acc.values,
                      [key]: formData[formDataKey].value,
                    },
                    errors: { ...acc.errors, [key]: error },
                  };
                },
                { values: {}, errors: {} }
              );
              const isValidForm = Object.values(formValidation.errors).every(
                (fieldError) => !fieldError
              );

              isValidData = isValidData && isValidForm;

              break;
            }
            case MOVE_DEVICE_OPTIONS.DEVICE_GROUP: {
              deviceGroupId = selectedDeviceGroup;

              isValidData = isValidData && !!selectedDeviceGroup;
              break;
            }
            case MOVE_DEVICE_OPTIONS.UNGROUPED: {
              if (!pendingEmptyGroupWarning) {
                await onRemoveDevicesFromDeviceGroup?.();
                return;
              }
            }
          }
          if (!isValidData) {
            return;
          }

          if (pendingEmptyGroupWarning) {
            setShowEmptyGroupWarning(true);
            return;
          }

          await onMoveDevicesToDeviceGroup({
            deviceGroupId,
            deviceGroupCode,
          });
        },
        customErrorHandler: (error) => {
          if (error.status === HttpStatusCode.NotFound)
            return toast.error(GROUP_CODE_NOT_FOUND_ERROR_MESSAGE);
          return toast.error(moveDevicesErrorMessage);
        },
      }),
    [
      deviceGroupCode,
      deviceGroupsToBeEmptied?.length,
      emptyGroupWarningAccepted,
      executeMoveDevices,
      formData,
      moveDevicesErrorMessage,
      onMoveDevicesToDeviceGroup,
      onRemoveDevicesFromDeviceGroup,
      selectedDeviceGroup,
      selectedMoveOption,
      showEmptyGroupWarning,
      toast,
    ]
  );

  const handleAcceptEmptyGroupWarning = useCallback(() => {
    setEmptyGroupWarningAccepted(true);
    handleMoveDevices();
  }, [handleMoveDevices]);

  const handleOnCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleMoveOptionItemClick = useCallback(
    (option: MOVE_DEVICE_OPTIONS) => {
      setSelectedMoveOption(option);
      setSelectedDeviceGroup(undefined);
      setSubmitButtonPressed(false);
    },
    []
  );

  useEffect(() => {
    if (!show) {
      resetData();
    }
  }, [resetData, show]);

  return (
    <Modal
      show={show}
      onClose={resetData}
      title={MODAL_TITLE}
      titleClassName="mb-4"
    >
      <Conditional
        condition={showEmptyGroupWarning && !!deviceGroupsToBeEmptied}
      >
        <Conditional.True>
          <RemoveDevicesModalContent
            deviceGroupsToBeEmptied={deviceGroupsToBeEmptied!}
            onRemoveDevicesFromDeviceGroup={handleAcceptEmptyGroupWarning}
            onCancel={handleOnCancel}
            loading={isMovingDevices}
            confirmationButtonText={warningButtonConfirmationText}
          />
        </Conditional.True>
        <Conditional.False>
          <Element
            paddingless
            display="flex"
            flexDirection="column"
            className="gap-8"
          >
            <h3 className="is-size-6">{subtitleText}</h3>

            <Element display="flex" flexDirection="column">
              <If
                condition={
                  !selectedMoveOption ||
                  selectedMoveOption === MOVE_DEVICE_OPTIONS.UNGROUPED
                }
              >
                <Element
                  display="flex"
                  flexDirection="column"
                  className="gap-6"
                >
                  {moveDevicesOptionsData.map((option) => (
                    <MoveDevicesOptionItem
                      key={option.value}
                      icon={option.icon}
                      label={option.value}
                      active={option.value === selectedMoveOption}
                      onClick={() => handleMoveOptionItemClick(option.value)}
                      hasNestedSelection={option.hasNestedSelection}
                    />
                  ))}
                </Element>
              </If>

              <If
                condition={
                  selectedMoveOption === MOVE_DEVICE_OPTIONS.DEVICE_GROUP
                }
              >
                <MoveDevicesDeviceGroupsLayout
                  deviceGroups={deviceGroups}
                  selectedDeviceGroupId={selectedDeviceGroup}
                  handleBackButtonClick={resetData}
                  handleSelectDeviceGroup={handleSelectDeviceGroup}
                />
              </If>

              <If
                condition={
                  selectedMoveOption === MOVE_DEVICE_OPTIONS.GROUP_CODE
                }
              >
                <MoveDevicesGroupCodeLayout
                  formData={formData}
                  setFormData={setFormData}
                />
              </If>

              <If condition={showErrorMessage}>
                <FeedbackMessage>
                  {OPTION_NOT_SELECTED_ERROR_MESSAGE}
                </FeedbackMessage>
              </If>
            </Element>

            <Element display="flex" justifyContent="space-between">
              <Conditional
                condition={
                  selectedMoveOption === MOVE_DEVICE_OPTIONS.GROUP_CODE
                }
              >
                <Conditional.True>
                  <Button color="primary-dark" outlined onClick={resetData}>
                    {BACK_BUTTON_TEXT}
                  </Button>
                </Conditional.True>
                <Conditional.False>
                  <Button
                    color="primary-dark"
                    outlined
                    onClick={handleOnCancel}
                  >
                    {CANCEL_BUTTON_TEXT}
                  </Button>
                </Conditional.False>
              </Conditional>
              <Button
                color="primary-dark"
                onClick={handleMoveDevices}
                loading={isMovingDevices}
                isExecutingAction={isMovingDevices}
              >
                {submitButtonText}
              </Button>
            </Element>
          </Element>
        </Conditional.False>
      </Conditional>
    </Modal>
  );
}

export default MoveDevicesModal;
